/**
 * useApi — universal data fetching hook
 * Barcha API chaqiruvlar uchun loading/error/data boshqaruvi
 *
 * Fayldagi ko'rinish komponentlari (Spinner, ErrorBox, EmptyState, SkeletonRow)
 * Monoline kit ustidagi yupqa qatlam: qattiq kodlangan ranglar olib tashlangan,
 * shuning uchun ular tungi mavzuda ham to'g'ri ko'rinadi.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { Alert, Button, EmptyState as KitEmptyState, Skeleton, Spinner as KitSpinner } from '../ui'

export function useApi(apiFn, deps = [], options = {}) {
  const { immediate = true, defaultData = null } = options
  const [data, setData]     = useState(defaultData)
  const [loading, setLoading] = useState(immediate)
  const [error, setError]   = useState(null)
  const mountedRef = useRef(true)

  useEffect(() => { return () => { mountedRef.current = false } }, [])

  const fetch = useCallback(async (...args) => {
    setLoading(true)
    setError(null)
    try {
      const result = await apiFn(...args)
      if (mountedRef.current) setData(result)
      return result
    } catch (e) {
      if (mountedRef.current) setError(e.message)
      return null
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [apiFn])

  useEffect(() => {
    if (immediate) fetch()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { data, loading, error, refetch: fetch, setData }
}

export function useMutation(apiFn) {
  const [loading, setLoading] = useState(false)
  const [error, setError]    = useState(null)

  const mutate = useCallback(async (...args) => {
    setLoading(true)
    setError(null)
    try {
      return await apiFn(...args)
    } catch (e) {
      setError(e.message)
      throw e
    } finally {
      setLoading(false)
    }
  }, [apiFn])

  return { mutate, loading, error }
}

/** Oddiy loading spinner — kitning `ui-spinner` i (rangni `currentColor` dan oladi) */
export function Spinner({ size = 20, ...rest }) {
  return <KitSpinner size={size} {...rest} />
}

/** Xato chiqarish komponenti — kitning `danger` ohangdagi Alert i */
export function ErrorBox({ error, onRetry }) {
  if (!error) return null
  return (
    <Alert tone="danger" title="Ma'lumot yuklanmadi" style={{ marginBottom: 16 }}>
      <div>{error}</div>
      {onRetry && (
        <div style={{ marginTop: 8 }}>
          <Button variant="danger" size="sm" onClick={onRetry}>Qayta urinish</Button>
        </div>
      )}
    </Alert>
  )
}

// Eslatma: ilgari bu yerda ../assets/empty.gif (2.5MB) statik import qilinardi.
// Endi yengil inline SVG ishlatiladi — gif fayl diskda qoldi, keyinchalik o'chirsa bo'ladi.
// Ranglar tokenlarga o'tkazildi: varaq va quti yuzasi `--ui-surface`,
// chiziqlar `--ui-text-3` — shuning uchun rasm tungi mavzuda oq dog' qoldirmaydi.
function EmptyIllustration() {
  return (
    <svg width="240" height="240" viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
         style={{ display: 'block', margin: '0 auto', userSelect: 'none', pointerEvents: 'none' }}>
      <circle cx="120" cy="120" r="82" fill="var(--ui-surface-2)" />
      {/* Suzuvchi varaqlar */}
      <rect x="66" y="58" width="34" height="42" rx="5" fill="var(--ui-surface)" stroke="var(--ui-text-3)" strokeWidth="2.5" transform="rotate(-10 83 79)" />
      <path d="M74 70 l18 -3 M75 78 l18 -3 M76 86 l12 -2" stroke="var(--ui-text-3)" strokeWidth="2.5" strokeLinecap="round" transform="rotate(-10 83 79)" />
      <rect x="140" y="56" width="34" height="42" rx="5" fill="var(--ui-surface)" stroke="var(--ui-text-3)" strokeWidth="2.5" transform="rotate(9 157 77)" />
      <path d="M148 68 l18 2 M147 76 l18 2 M146 84 l12 1.5" stroke="var(--ui-text-3)" strokeWidth="2.5" strokeLinecap="round" transform="rotate(9 157 77)" />
      {/* Ochiq quti (bo'sh javon) */}
      <path d="M62 122 h116 v46 a10 10 0 0 1 -10 10 H72 a10 10 0 0 1 -10 -10 z" fill="var(--ui-surface)" stroke="var(--ui-text-3)" strokeWidth="3" strokeLinejoin="round" />
      <path d="M62 122 L78 100 h84 l16 22" fill="none" stroke="var(--ui-text-3)" strokeWidth="3" strokeLinejoin="round" />
      <path d="M62 122 l28 16 h60 l28 -16" fill="none" stroke="var(--ui-text-3)" strokeWidth="3" strokeLinejoin="round" />
      {/* Bezak nuqtalar */}
      <circle cx="52" cy="96" r="4" fill="var(--ui-border-2)" />
      <circle cx="192" cy="104" r="3" fill="var(--ui-border-2)" />
      <circle cx="120" cy="46" r="3.5" fill="var(--ui-border-2)" />
    </svg>
  )
}

/**
 * Bo'sh holat — kitning EmptyState i.
 *
 * Sarlavha `title` yoki eski `text` orqali, izoh `subtitle` yoki eski `subtext`
 * orqali beriladi (ikkala chaqiruv uslubi ham kodbazada uchraydi).
 * `icon` bo'sh bo'lsa yuqoridagi illyustratsiya chiziladi.
 * Eski versiyadagi blok bo'ylab `pointerEvents: 'none'` olib tashlandi —
 * u `action` tugmasini bosib bo'lmaydigan qilib qo'yardi.
 */
export function EmptyState({ icon, title, text, subtitle, subtext, action }) {
  const heading = title || text || 'Ma\'lumot topilmadi'
  const body = subtitle || subtext || 'Hozircha bu bo\'limda hech qanday ma\'lumot mavjud emas.'
  // Kit `icon` da komponent kutadi; tugun kelsa uni komponentga o'raymiz.
  let Art = EmptyIllustration
  if (typeof icon === 'function') Art = icon
  else if (icon) Art = () => icon

  return <KitEmptyState icon={Art} title={heading} text={body} action={action} />
}

/**
 * Yuklanish skeleti (jadval qatori).
 * Kengliklar barqaror ro'yxatdan olinadi — Math.random() har renderda
 * qayta hisoblanib, qatorlar sakrab turardi.
 */
const SKELETON_WIDTHS = ['82%', '64%', '76%', '58%', '88%', '68%', '72%', '60%']

export function SkeletonRow({ cols = 5 }) {
  return (
    <tr>
      {Array.from({ length: cols }, (_, i) => (
        <td key={i} style={{ padding: '12px 16px' }}>
          <Skeleton variant="text" width={SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]} />
        </td>
      ))}
    </tr>
  )
}
